import { Box, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import { msspDashboardKeys } from "../../../../api/queryKeys";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ComplexEntityTypes } from "../../../../utils/constants";
import { EntityTypeInfo } from "../../../stack/environment/components/constants";
import AssetCard from "./AssetCard";
import DonutPieChart from "./DonutPieChart";

const AssetsBreakdown = () => {
  const colors = useColors();

  const { getMSSPAssetsBreakdown } = useAPI();

  const { data: assetsBreakdownData, isLoading: isLoadingAssetsBreakdown } =
    useQuery({
      queryKey: msspDashboardKeys.assetsBreakdown(),
      queryFn: getMSSPAssetsBreakdown,
      select: (data) => data.data,
    });

  const colorsByEntityType = {
    [ComplexEntityTypes.HOST]: colors.primary_500,
    [ComplexEntityTypes.USER]: colors.primary_main,
    [ComplexEntityTypes.SERVER]: colors.primary_100,
    [ComplexEntityTypes.DATA_STORE]: colors.grey_400,
    [ComplexEntityTypes.NONHUMAN_IDENTITY]: colors.primary_48percent,
  };

  const assetsBreakdownDataToDisplay =
    assetsBreakdownData &&
    Object.entries(assetsBreakdownData)
      ?.filter(([key]) => key !== "Tenants")
      ?.map(([key, value], index) => ({
        id: index,
        name: key,
        value: value,
        label: EntityTypeInfo[key]?.title + "s",
        color: colorsByEntityType[key],
      }));

  const tenantsDataToDisplay = assetsBreakdownData?.Tenants
    ? {
        id: "Tenants",
        name: "Tenants",
        value: assetsBreakdownData.Tenants,
        label: "Tenants",
      }
    : null;

  return (
    <Box
      sx={{
        backgroundColor: colors.common_white,
        borderRadius: "10px",
        boxShadow: "0px 0px 2px 1px " + colors.mssp_box_shadow_color,
        padding: "16px",
        display: "flex",
        flexDirection: "column",
        gap: "40px",
      }}
    >
      <Typography variant="subtitle1">Assets Breakdown</Typography>
      {isLoadingAssetsBreakdown ? (
        <LoadingIcon />
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-around",
            padding: "0 101px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "120px",
            }}
          >
            <Box
              sx={{
                maxWidth: "606px",
                display: "flex",
                flexDirection: "row",
                gap: 3,
                flexWrap: "wrap",
              }}
            >
              {tenantsDataToDisplay && (
                <AssetCard
                  key={tenantsDataToDisplay.name}
                  {...tenantsDataToDisplay}
                />
              )}
              {assetsBreakdownDataToDisplay?.map((card) => (
                <AssetCard key={card.name} {...card} />
              ))}
            </Box>
            <Box>
              <DonutPieChart data={assetsBreakdownDataToDisplay} />
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AssetsBreakdown;
